import { francAll } from "franc-min"

// franc returns ISO 639-3 codes; the browser Translator API expects BCP 47.
// Keep this to languages Chrome's Translator currently supports.
const browserLanguages: Record<string, string> = {
  bul: "bg",
  ces: "cs",
  deu: "de",
  eng: "en",
  fra: "fr",
  hun: "hu",
  ita: "it",
  lit: "lt",
  nld: "nl",
  pol: "pl",
  ron: "ro",
  rus: "ru",
  slk: "sk",
  spa: "es",
  tur: "tr",
  ukr: "uk",
}

type TranslatorSession = {
  translate: (text: string) => Promise<string>
  destroy: () => void
}

type BrowserTranslator = {
  create: (options: {
    sourceLanguage: string
    targetLanguage: string
    monitor: (monitor: {
      addEventListener: (
        type: "downloadprogress",
        listener: (event: { loaded: number }) => void,
      ) => void
    }) => void
  }) => Promise<TranslatorSession>
}

const browserTranslator = (): BrowserTranslator | undefined =>
  (globalThis as typeof globalThis & { Translator?: BrowserTranslator })
    .Translator

export const canTranslateInBrowser = (): boolean =>
  typeof browserTranslator()?.create === "function"

/** Choose the original when its language is sufficiently clear. */
export const browserTranslationInput = (original: string, polish: string) => {
  const [best, next] = francAll(original, { minLength: 25 })
  const sourceLanguage =
    best && best[1] - (next?.[1] ?? 0) >= 0.1
      ? browserLanguages[best[0]]
      : undefined

  return sourceLanguage
    ? { text: original, sourceLanguage }
    : { text: polish, sourceLanguage: "pl" }
}

/** Call this directly from the click handler: create() needs user activation. */
export const translateIncomingMessageToEnglish = async (
  original: string,
  polish: string,
  onDownloadProgress: (progress: number) => void,
): Promise<string> => {
  const api = browserTranslator()
  if (!api) throw new Error("Browser translation is unavailable")

  const { text, sourceLanguage } = browserTranslationInput(original, polish)
  if (sourceLanguage === "en") return text

  // Do not await availability() here: activation may expire before create().
  const session = await api.create({
    sourceLanguage,
    targetLanguage: "en",
    monitor(monitor) {
      monitor.addEventListener("downloadprogress", (event) => {
        onDownloadProgress(Math.round(event.loaded * 100))
      })
    },
  })

  try {
    return await session.translate(text)
  } finally {
    session.destroy()
  }
}
